import Vue from 'vue';
import { i18n } from '@/plugins/vue-i18n';
import { typeToIcon } from '@/modules/content/config';
import {
  chip,
  tooltip,
  metadata,
  flag,
  Actions,
} from '@/modules/list/config/utils';
import store from '@/store';

export default function (content) {
  const bannedSourceCodes = [
    'bCube',
    'legacy',
  ];

  const sourceCode = bannedSourceCodes.includes(content.sourceCode) ? null : i18n.t(`message.content.source.${content.sourceCode}`);
  const firstPublicationDate = content.firstPublicationDate ? Vue.filter('formatDateFromNow')(content.firstPublicationDate) : null;
  const lastPublicationDate = content.lastPublicationDate ? Vue.filter('formatDateFromNow')(content.lastPublicationDate) : null;
  const publishedDate = (
    content.published
    && !content.firstPublicationDate
    && !content.lastPublicationDate
    && !content.unpublishedDate)
    ? Vue.filter('formatDateFromNow')(content.publishedDate)
    : null;
  const draft = !content.proccessing && !content.scheduled && !content.published ? i18n.t('message.content.status.draft') : null;
  const unpublishedDate = !content.published && content.unpublishedDate
    ? `${i18n.t('message.content.status.unpublishedDate')} ${Vue.filter('formatDate')(content.unpublishedDate)}`
    : null;
  const scheduleUnpublish = content.scheduleUnpublish
    ? `${i18n.t('message.content.status.scheduleUnpublish')} ${Vue.filter('formatDate')(content.scheduleUnpublish)}`
    : null;

  // Metatadata

  let metaStatus = null;
  if (content.proccessing) {
    metaStatus = i18n.t('message.content.status.processing');
  } else if (content.scheduled) {
    metaStatus = i18n.t('message.content.status.scheduled');
  } else if (content.published) {
    metaStatus = i18n.t('message.content.status.published');
  } else {
    metaStatus = i18n.t('message.content.status.draft');
  }

  let publishedMetaLabel = 'publishDate';
  let publishedMetaValue = null;
  if (content.published) {
    if (content.firstPublicationDate) {
      publishedMetaLabel = i18n.t('message.metadata.firstPublicationDate');
      publishedMetaValue = Vue.filter('formatDate')(content.firstPublicationDate);
    } else {
      publishedMetaLabel = i18n.t('message.metadata.publishedDate');
      publishedMetaValue = Vue.filter('formatDate')(content.publishedDate);
    }
  }

  const site = store.getters.getSiteFromSiteId(content.siteId);

  return {
    id: content.id,
    title: content.title,
    image: {
      src: content.largeThumbnailUrl || (content.clipping ? content.clipping.url : null),
      alt: content.title,
    },
    icon: {
      name: typeToIcon(content.type),
    },
    flag: flag(content),
    editor: 'content',
    type: content.type,
    url: content.publicUrl,
    chips: [
      tooltip(
        chip(
          i18n.tc(`message.content.type.${content.type}`, 1).toUpperCase().slice(0, 3),
          '#4e82d1',
          '#ffffff',
          null,
          { name: 'add-filter-search', field: 'type', value: content.type },
        ),
        i18n.tc(`message.content.type.${content.type}`, 1),
      ),
      tooltip(chip(publishedDate), i18n.t('message.metadata.published')),
      tooltip(chip(content.id, null, null, null, { name: 'clipboard-copy', value: content.id }), i18n.t('message.metadata.id')),
      tooltip(chip(sourceCode), i18n.t('message.metadata.origin')),
      tooltip(chip(content.taxonomy ? content.taxonomy.name : null, null, null, null, { name: 'add-filter-search', field: 'taxon', value: content.taxonomy ? content.taxonomy.id : null }), i18n.t('message.metadata.mainCategory')),
      tooltip(chip(firstPublicationDate), i18n.t('message.metadata.firstPublicationDate')),
      tooltip(chip(lastPublicationDate), i18n.t('message.metadata.lastPublicationDate')),
      tooltip(
        chip(content.proccessing ? i18n.t('message.content.status.processing') : null),
        i18n.t('message.metadata.status'),
      ),
      tooltip(
        chip(!content.proccessing && content.scheduled ? i18n.t('message.content.status.scheduled') : null),
        i18n.t('message.metadata.status'),
      ),
      tooltip(
        chip(!content.proccessing && !content.scheduled && content.published ? i18n.t('message.content.status.published') : null, null, null, null, { name: 'add-filter-search', field: 'state', value: 'published' }),
        i18n.t('message.metadata.status'),
      ),
      tooltip(chip(draft, null, null, null, { name: 'add-filter-search', field: 'state', value: 'not-published' }), i18n.t('message.metadata.draft')),
      tooltip(chip(unpublishedDate), i18n.t('message.metadata.unpublishDate')),
      tooltip(chip(scheduleUnpublish), i18n.t('message.metadata.scheduleUnpublished')),
      tooltip(
        chip(
          content.creationStaffId ? content.creationStaffId.nick : null,
          null,
          null,
          {
            src: content.creationStaffId ? content.creationStaffId.avatarUrl : null,
            alt: content.creationStaffId ? content.creationStaffId.nick : null,
          },
          { name: 'add-filter-search', field: 'staffCreators', value: content.creationStaffId ? content.creationStaffId.id : null },
        ),
        i18n.t('message.metadata.creationStaffId'),
      ),
    ].filter(c => !!c.value),
    metadata: [
      metadata('ID', content.id),
      metadata(i18n.t('message.content.status.status'), metaStatus),
      metadata(publishedMetaLabel, publishedMetaValue),
      metadata(i18n.t('message.metadata.publicUrl'), content.published && content.publicUrl ? content.publicUrl : null, true),
      metadata(i18n.t('message.metadata.createdBy'), content.creationStaffId ? content.creationStaffId.nick : null),
      metadata(i18n.t('message.metadata.creationSite'), site ? site.name : null),
      metadata(i18n.t('message.metadata.creationDate'), Vue.filter('formatDate')(content.creationDate)),
      metadata(i18n.t('message.metadata.source'), i18n.t(`message.content.source.${content.sourceCode}`)),
      content.published
        ? metadata(
          content.lastPublicationDate ? i18n.t('message.metadata.lastPublicationDate') : i18n.t('message.metadata.publishedDate'),
          content.lastPublicationDate ? Vue.filter('formatDate')(content.lastPublicationDate) : Vue.filter('formatDate')(content.publishedDate),
        )
        : metadata(
          i18n.t('message.metadata.lastModifiedDate'),
          Vue.filter('formatDate')(content.modificationDate),
        ),
      metadata(i18n.t('message.metadata.lastModifiedBy'), content.lastModificationStaffId ? content.lastModificationStaffId.nick : null),
    ],
    actions: [
      Actions.ADD_BAG,
      Actions.METADATA,
      Actions.LINK,
    ],
  };
}
